import { useMemo, useState } from "react";
import { BRANDED_LOADER_ICON_POOL } from "@corpuscomplete/core/common/branded-loader";
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useTime,
  useTransform,
} from "framer-motion";
import { FoodIcon } from "@corpuscomplete/web/components/FoodIcon";
import { cn } from "@corpuscomplete/web-ui/lib/utils";

const SPOKE_COUNT = 7;
const SPOKE_DURATION_MS = 600;
const DISTANCE_PX = 74;
const SWAP_HIDE_DISTANCE_PX = 8;
const ACTIVE_WINDOW = 0.35;

type BrandedLoaderProps = {
  className?: string;
};

export function BrandedLoader({ className }: BrandedLoaderProps) {
  const prefersReducedMotion = useReducedMotion();
  const icons = useMemo(() => shuffle([...BRANDED_LOADER_ICON_POOL]), []);
  const spokeActiveFraction = getSpokeActiveFraction(ACTIVE_WINDOW);

  return (
    <div className={cn("relative h-64 w-64", className)} aria-hidden>
      <div className="absolute inset-0">
        {Array.from({ length: SPOKE_COUNT }, (_, spoke) => (
          <BrandedLoaderSpoke
            key={spoke}
            icons={icons}
            spoke={spoke}
            spokeActiveFraction={spokeActiveFraction}
            isReducedMotion={Boolean(prefersReducedMotion)}
          />
        ))}
      </div>

      <div className="absolute left-1/2 top-1/2 z-[15] h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background" />

      <div className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
        <FoodIcon slug={null} size="lg" decorative className="size-18 drop-shadow-lg" />
      </div>
    </div>
  );
}

function BrandedLoaderSpoke({
  icons,
  spoke,
  spokeActiveFraction,
  isReducedMotion,
}: {
  icons: readonly string[];
  spoke: number;
  spokeActiveFraction: number;
  isReducedMotion: boolean;
}) {
  const startTime = useMemo(() => performance.now(), []);
  const slotOffset = spoke / SPOKE_COUNT;
  const time = useTime();
  const angle = (spoke / SPOKE_COUNT) * Math.PI * 2 - Math.PI / 2;
  const [cycle, setCycle] = useState(() => Math.floor(-slotOffset));

  const timeline = useTransform(time, (latest) => {
    if (isReducedMotion) {
      return 0;
    }

    return (latest - startTime) / (SPOKE_DURATION_MS * SPOKE_COUNT);
  });

  const phase = useTransform(timeline, (value) => {
    if (isReducedMotion) {
      return 0.72;
    }

    const masterProgress = modulo(value, 1);
    const localProgress = modulo(masterProgress - slotOffset, 1);
    if (localProgress > spokeActiveFraction) {
      return null;
    }

    return localProgress / spokeActiveFraction;
  });

  useMotionValueEvent(time, "change", (latest) => {
    if (isReducedMotion) {
      return;
    }

    const nextCycle = Math.floor(
      (latest - startTime) / (SPOKE_DURATION_MS * SPOKE_COUNT) - slotOffset,
    );
    setCycle((current) => (current === nextCycle ? current : nextCycle));
  });

  const distance = useTransform(phase, (value) => {
    if (value === null) {
      return 0;
    }

    return Math.sin(value * Math.PI) * DISTANCE_PX;
  });
  const x = useTransform(distance, (value) => Math.cos(angle) * value);
  const y = useTransform(distance, (value) => Math.sin(angle) * value);
  const opacity = useTransform(phase, (value) => {
    if (isReducedMotion) {
      return 1;
    }

    if (value === null) {
      return 0;
    }

    return Math.sin(value * Math.PI) * DISTANCE_PX > SWAP_HIDE_DISTANCE_PX ? 1 : 0;
  });
  const slug = icons[modulo(spoke * 3 + cycle, icons.length)] ?? null;

  return (
    <div className="absolute left-1/2 top-1/2 z-10">
      <div className="-translate-x-1/2 -translate-y-1/2">
        <motion.div style={{ x, y, opacity }}>
          <FoodIcon
            slug={slug}
            size="md"
            decorative
            className="size-9 drop-shadow-[0_8px_18px_rgba(15,23,42,0.18)]"
          />
        </motion.div>
      </div>
    </div>
  );
}

function shuffle<T>(items: T[]) {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    const item = items[index];
    const swapItem = items[swapIndex];
    if (item === undefined || swapItem === undefined) {
      continue;
    }
    items[index] = swapItem;
    items[swapIndex] = item;
  }

  return items;
}

function modulo(value: number, base: number) {
  return ((value % base) + base) % base;
}

function getSpokeActiveFraction(activeWindow: number) {
  return 1 / SPOKE_COUNT + activeWindow * (1 - 1 / SPOKE_COUNT);
}
